import { Stack } from '@mui/material';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type SurfaceContent = React.ReactNode;

type SnackbarRenderContextValue = {
  register: (id: string) => void;
  unregister: (id: string) => void;
  setContent: (id: string, content: SurfaceContent) => void;
};

const SnackbarRenderContext =
  React.createContext<SnackbarRenderContextValue | null>(null);

export function SnackbarRenderProvider(props: React.PropsWithChildren) {
  const { children } = props;
  const [surfaces, setSurfaces] = useState<Record<string, SurfaceContent>>({});

  const register = useCallback((id: string) => {
    setSurfaces((prev) =>
      prev[id] !== undefined ? prev : { ...prev, [id]: null },
    );
  }, []);

  const unregister = useCallback((id: string) => {
    setSurfaces((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const setContent = useCallback((id: string, content: SurfaceContent) => {
    setSurfaces((prev) =>
      prev[id] !== undefined ? { ...prev, [id]: content } : prev,
    );
  }, []);

  const value = useMemo<SnackbarRenderContextValue>(
    () => ({ register, unregister, setContent }),
    [register, unregister, setContent],
  );

  const entries = Object.entries(surfaces);

  return (
    <SnackbarRenderContext.Provider value={value}>
      {children}
      <Stack
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column-reverse',
          alignItems: 'flex-end',
          gap: 8,
          pointerEvents: 'none',
        }}
      >
        {entries.map(
          ([id, content]) =>
            content && (
              <Stack
                sx={{
                  bgcolor: 'background.paper',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  boxShadow: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
                key={id}
              >
                {content}
              </Stack>
            ),
        )}
      </Stack>
    </SnackbarRenderContext.Provider>
  );
}

function usePersistentId() {
  const [id, setId] = useState('');

  useEffect(() => {
    setId(crypto.randomUUID());
  }, []);

  return id;
}

export function useSnackbarSurface(): (node: React.ReactNode) => null {
  const ctx = useContext(SnackbarRenderContext);
  const id = usePersistentId();

  useMemo(() => {
    if (ctx) ctx.register(id);
  }, [ctx, id]);

  const unmount = useMemo(
    () => () => {
      if (ctx) ctx.unregister(id);
    },
    [ctx, id],
  );

  useEffect(() => unmount, [unmount]);

  const render = useCallback(
    (node: React.ReactNode): null => {
      ctx?.setContent(id, node);
      return null;
    },
    [ctx, id],
  );

  return render;
}
